export const MembersTable = {
  state: {
    memberRoleBeingEdited: null,
    successMessage: null,
    errorMessage: null,
    projectMembers: [],
    toUpdate: null
  },
  // The header row. Its key names the component it is, Grid (FA806).
  Grid: {
    columns: '2.2em 1fr 2fr 1fr 1fr',
    padding: 'A1',
    // Dense data table (§2.5 exception #3) — sticky header underline kept,
    // fixed to a real token: was a bare `--color-line` custom-property name
    // with no var() wrapper, an invalid border-bottom shorthand value.
    borderBottom: 'hairline solid line',
    fontWeight: 'bold',
    // The tonal header plate (brief §4.5 item 1, 2026-09-24). 'background-alt'
    // exists in no brand, workspace or framework file: the fill was a silent
    // no-op. `widgetPlate` is the plate AppTableHead uses.
    background: 'widgetPlate',
    AvatarHeader: {
      text: ''
    },
    NameHeader: {
      text: '{{ members.table.name | polyglot }}'
    },
    EmailHeader: {
      text: '{{ members.table.email | polyglot }}'
    },
    RoleHeader: {
      // Names every row's role select (aria-labelledby, below) — one id per
      // table, from the header's own key path.
      id: (el) =>
        'members-role-' +
        el
          .getPath()
          .join('-')
          .replace(/[^A-Za-z0-9_-]/g, ''),
      text: '{{ members.table.role | polyglot }}'
    },
    ActionsHeader: {
      show: (el) => el.isAdmin(),
      text: '{{ members.table.actions | polyglot }}'
    }
  },
  TableBody: {
    childProps: {
      Avatar: {
        boxSize: 'B',
        text: '{{name}}'
      },
      NameCell: {
        // Per-row id: the second half of the row's role-select name.
        id: (el) =>
          'members-name-' +
          el
            .getPath()
            .join('-')
            .replace(/[^A-Za-z0-9_-]/g, ''),
        text: '{{name}}'
      },
      EmailCell: {
        text: '{{email}}'
      },
      RoleCell: {
        HiddenElement: {
          show: false,
          tag: 'div',
          display: 'none',
          text: ''
        },
        SelectField: {
          show: (el, s) => s.parent.memberRoleBeingEdited === s.id,
          // The brand SelectField owns the field's shape and padding: a
          // rounded rect at `radiusControl`, not a capsule (DESIGN_LANGUAGE
          // §6.2), and no padding on the root. This call site restated
          // `padding: '0'` and overrode the shape to a `radiusPill` capsule.
          position: 'relative',
          Select: {
            // Named "Role, <member>" by the column header and this row's name
            // cell — no new copy. It had no name at all, and the literal id and
            // name 'user' on every row's select (the brand SelectField now
            // gives each its own id, 3.14.796).
            // Each id is the element's OWN `id` prop, run the way DOMQL runs it
            // (`exec`) — never read off the node, which can still hold the
            // unevaluated prop while this runs (measured: the name came out as
            // the id functions' source text).
            ariaLabelledby: (el) => {
              const row = el.parent.parent.parent
              const table = row.parent.parent
              const header = table.Grid && table.Grid.RoleHeader
              const idOf = (x) => (x && x.id ? el.call('exec', x.id, x) : null)
              return [idOf(header), idOf(row.NameCell)].filter(Boolean).join(' ') || null
            },
            onChange: (ev, el, s) => {
              ev.preventDefault()
              ev.stopImmediatePropagation()
              s.parent.update({
                toUpdate: {
                  id: s.id,
                  newRole: ev.target.value
                }
              })
            },
            childrenAs: 'props',
            children: () => [
              {
                value: 'owner',
                text: '{{ members.role.owner | polyglot }}'
              },
              {
                value: 'admin',
                text: '{{ members.role.admin | polyglot }}'
              },
              {
                value: 'editor',
                text: '{{ members.role.editor | polyglot }}'
              },
              {
                value: 'guest',
                text: '{{ members.role.guest | polyglot }}'
              }
            ],
            childProps: {
              tag: 'option',
              disabled: (el) => !el.isAdmin(),
              // `selected` stays in `attr`: there it replaces the brand
              // SelectField's own `attr.selected` factory. A flat `selected`
              // would leave that factory running beside this one.
              attr: {
                selected: (el, s) => s.role === el.value
              }
            }
          }
        },
        CurrentRole: {
          show: (el, s) => s.parent.memberRoleBeingEdited !== s.id,
          text: '{{role}}'
        }
      },
      ActionsCell: {
        flow: 'x',
        childExtends: 'IconButton',
        show: (el) => el.isAdmin(),
        gap: 'A1',
        EditButton: {
          show: (el, s) => el.isAdmin(),
          background: 'transparent',
          color: 'inactive',
          // Adaptive ink-surface pair (was fixed 'gray4'/'white') — a solid
          // hover fill flips polarity with theme, same fix as NativeSelect.
          ':hover': {
            background: 'highlight',
            color: 'highlight-reversed'
          },
          icon: 'edit',
          aria: { label: (el) => el.call('polyglot', 'members.table.editRole') },
          onClick: (ev, el, s) => {
            s.parent.update({
              memberRoleBeingEdited: s.id,
              toUpdate: null
            })
          }
        },
        ConfirmUpdateRoleButton: {
          show: (el, s) => s.parent.memberRoleBeingEdited === s.id,
          background: 'transparent',
          color: 'inactive',
          icon: 'check',
          aria: { label: (el) => el.call('polyglot', 'members.table.confirmRole') },
          // Adaptive ink-surface pair — see EditButton above.
          ':hover': {
            background: 'highlight',
            color: 'highlight-reversed'
          },
          onClick: async (ev, el, s) => {
            ev.preventDefault()
            ev.stopImmediatePropagation()
            if (!s.parent.toUpdate) {
              return
            }
            try {
              const { id: memberId, newRole } = s.parent.toUpdate

              if (newRole) {
                // Call API to update member role
                await el.sdk.updateMemberRole(s.root.projectId, memberId, newRole)

                s.parent.update({
                  memberRoleBeingEdited: null,
                  toUpdate: null,
                  successMessage: 'Member role updated successfully!'
                })

                // Refresh the table after a short delay
                setTimeout(async () => {
                  const result = await el.sdk.getProjectMembers(s.root.projectId)
                  s.parent.replace({
                    projectMembers:
                      result.map((r) => ({
                        ...r,
                        id: r.user.id,
                        email: r.user.email,
                        name: r.user.name || r.user.email,
                        role: r.role
                      })) || []
                  })
                }, 500)
              }
            } catch (error) {
              s.parent.update({
                errorMessage: `Error: ${error.message || 'Failed to update member role'}`,
                memberRoleBeingEdited: null,
                toUpdate: null
              })
            }
          }
        },
        CrossmarkButton: {
          show: (el, s) => s.parent.memberRoleBeingEdited === s.id,
          background: 'transparent',
          color: 'inactive',
          // Adaptive ink-surface pair — see EditButton above.
          ':hover': {
            background: 'highlight',
            color: 'highlight-reversed'
          },
          icon: 'crossmark',
          aria: { label: (el) => el.call('polyglot', 'common.action.cancel') },
          onClick: (ev, el, s) => {
            s.parent.update({
              memberRoleBeingEdited: null,
              toUpdate: null
            })
          }
        },
        TrashButton: {
          // FA515: `show` reads s.root.projectRole only on a non-owner row (the
          // `&&` returns first on an owner row), so that read was never tracked
          // there. The declared selector reads it on every run.
          showDeps: [(el, s) => s.root && s.root.projectRole],
          show: (el, s) => s.role !== 'owner' && ['owner', 'admin'].includes(s.root.projectRole),
          background: 'transparent',
          color: 'inactive',
          // Adaptive ink-surface pair — see EditButton above.
          ':hover': {
            background: 'highlight',
            color: 'highlight-reversed'
          },
          icon: 'trash',
          aria: { label: (el) => el.call('polyglot', 'members.table.removeMember') },
          onClick: async (ev, el, s) => {
            const memberId = s.id
            ev.preventDefault()
            ev.stopImmediatePropagation()

            const memberName = s.name

            const res = window.confirm(
              `Do you really want to remove ${memberName} from this project?`
            )

            if (res) {
              try {
                await el.sdk.removeMember(el.getRootState('projectId'), memberId)
                s.parent.update({
                  successMessage: `${memberName} has been removed from the project.`
                })
              } catch (err) {
                s.parent.update({
                  errorMessage: `Error: ${err.message || 'Failed to remove member'}`
                })
              }
            }
          }
        }
      },
      columns: '2.2em 1fr 2fr 1fr 1fr',
      width: '100%',
      align: 'center center',
      padding: 'A1',
      // Row separator inside the same dense table (§2.5 exception #3) — was a
      // raw '1px' width against `var(--color-border-light)`, a custom
      // property that is never defined anywhere in the design system, so the
      // border rendered with no colour at all. Fixed to the real `line`
      // token; hover fixed to the `hover` interaction wash (§4.3) — its
      // `var(--color-background-hover)` was equally undefined.
      borderBottom: 'hairline solid line',
      ':hover': {
        background: 'hover'
      }
    },
    flow: 'y',
    width: '100%',
    childExtends: 'Grid',
    childrenAs: 'state',
    children: (el, s) => s.projectMembers
  },
  EmptyMessage: {
    show: (el, s) => !s.projectMembers || s.projectMembers.length === 0,
    text: '{{ members.table.empty | polyglot }}',
    padding: 'B1',
    textAlign: 'center',
    opacity: '0.7'
  },
  flow: 'y',
  width: '100%',
  margin: 'B1 0',
  overflow: 'hidden',
  onRender: async (el, s) => {
    // No SDK in this host (UI-kit catalog, preview frame, spec host) → the
    // table keeps its declared empty state and EmptyMessage shows. The
    // member load is a data enhancement, not a precondition for rendering.
    if (!el.sdk?.getProjectMembers) return
    const result = await el.sdk.getProjectMembers(el.getRootState('projectId'))
    s.update({
      projectMembers: (result || []).map((r) => {
        const user = r.user || {}
        return {
          ...r,
          id: r.id,
          email: user.email,
          name: user.name || user.email,
          role: r.role
        }
      })
    })
  },
  // ——— the toast watcher (WS-DEAD-ONSTATEUPDATE-HOOKS-SIBLINGS-1) ———
  //
  // `onStateUpdate` is a DECLARATIVE effect in DOMQL v3.14: the framework
  // registers it ONLY when a sibling `stateDeps` ARRAY exists, and no legacy
  // `stateUpdate` event is emitted anywhere. This block shipped with NO
  // `stateDeps` and the legacy `(changes, el)` signature, so it never ran:
  // a role update and a member removal both wrote `successMessage` /
  // `errorMessage` and NO notification ever opened.
  //
  // Each selector is self-contained (FA204 — a `stateDeps` entry is
  // serialized on its own by frank.toJSON, so a shared module-scope helper
  // would read as `undefined` on the platform payload). Both keys live on
  // THIS component's own state, written by the row buttons above.
  stateDeps: [(el, s) => (s || {}).successMessage, (el, s) => (s || {}).errorMessage],
  // v3.14 signature: `(el, state, context, { prev, next })`. The effect only
  // runs when a selector value actually changed, so an unchanged message is
  // never re-toasted; a truthiness guard drops the write back to `null`.
  onStateUpdate: (el, s, ctx, { prev, next }) => {
    const [successMessage, errorMessage] = next
    if (successMessage && successMessage !== prev[0]) {
      el.call('openNotification', {
        title: el.call('polyglot', 'members.notify.success.title'),
        message: successMessage,
        type: 'success'
      })
    }
    if (errorMessage && errorMessage !== prev[1]) {
      el.call('openNotification', {
        title: el.call('polyglot', 'members.notify.error.title'),
        message: errorMessage,
        type: 'error'
      })
    }
  }
}
